import '@shui/core/sh-tabs';

import { Component, input, booleanAttribute, ChangeDetectionStrategy } from '@angular/core';

@Component({
    selector: 'sh-tabs',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.active]': 'active() ? "" : null',
        '[attr.collapsed]': 'collapsed() ? "" : null',
        '[attr.condensed]': 'condensed() ? "" : null',
        '[attr.fixed]': 'fixed() ? "" : null',
        '[attr.icon]': 'icon() ?? null',
        '[attr.info]': 'info() ?? null',
        '[attr.label]': 'label() ?? null',
        '[attr.neutral]': 'neutral() ? "" : null',
        '[attr.no-arrows]': 'noArrows() ? "" : null',
        '[attr.no-scroll]': 'noScroll() ? "" : null',
        '[attr.override]': 'override() ?? null',
        '[attr.scrollable]': 'scrollable() ? "" : null'
    }
})
export class ShTabsComponent {
    /** If collapsed is set to true, this property defines if the dropdown menu is open or not. */
    public readonly active = input(false, { transform: booleanAttribute });

    /**
     * Collapses the tab selection into a dropdown menu. Automatically triggered when tabs are used
     * in the context of an Access Bar and the screen is smaller than 1024.
     */
    public readonly collapsed = input(false, { transform: booleanAttribute });

    /** Adds condensed styles if set to `true`. */
    public readonly condensed = input(false, { transform: booleanAttribute });

    /**
     * If set to `true`, the tabs will not be collapsed when placed inside the Access Bar for small
     * screen sizes.
     */
    public readonly fixed = input(false, { transform: booleanAttribute });

    /**
     * If collapsed is set to true, this property defines the icon shown at a fixed position. The
     * value of this property is updated on load and every time a new tab-item is selected.
     */
    public readonly icon = input<string | number | undefined>(undefined);

    /**
     * If collapsed is set to true and the active tab-item is a patient item, this property defines
     * the info shown below the label. The value of this property is updated on load and every time
     * a new tab-item is selected.
     */
    public readonly info = input<string | number | undefined>(undefined);

    /**
     * If collapsed is set to true, this property defines the label shown at a fixed position. The
     * value of this property is updated on load and every time a new tab-item is selected.
     */
    public readonly label = input<string | number | undefined>(undefined);

    /**
     * If set, neutral tab variantion does not use the primary orange color for highlighting & uses
     * a more subtle visual style. It has no effect on collapsed tab.
     */
    public readonly neutral = input(false, { transform: booleanAttribute });

    /**
     * If `scrollable` is set to true, this property can be used in a touch environment, where
     * scrolling horizontally is intuitive and the arrows can be dismissed due to lack of space.
     */
    public readonly noArrows = input(false, { transform: booleanAttribute });

    /**
     * If `no-scroll` is set to true, the tabs will not scroll horizontally when there are too many
     * tabs across the viewport.
     */
    public readonly noScroll = input(false, { transform: booleanAttribute });

    public readonly override = input<string | number | undefined>(undefined);

    /**
     * If set to `true`, the tabs will scroll horizontally when there are too many tabs across the
     * viewport.
     */
    public readonly scrollable = input(false, { transform: booleanAttribute });
}
